import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Plus, X, Archive, Upload, Download } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

export function AuctionItemsTab({ items, setItems, lots, eventRef }) {
  const [importMode, setImportMode] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  const addItem = () => {
    setItems([...items, {
      item_id: `ai_${Date.now()}`, line_number: items.length + 1,
      description: '', quantity: 1, uom: 'EA', estimated_price: 0, lot_id: '', specifications: '',
    }]);
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/api/rfx/items-import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'rfx_items_template.xlsx'; a.click();
    } catch { toast.error('Failed to download template'); }
  };

  const processFile = async (file) => {
    setImportLoading(true);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      setImportData(rows);
      setImportMode(true);
      toast.success(`${rows.length} row(s) parsed from ${file.name}`);
    } catch (err) { toast.error('Failed to parse spreadsheet: ' + err.message); }
    setImportLoading(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    e.target.value = '';
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      processFile(file);
    } else if (file) {
      toast.error('Please drop an Excel (.xlsx, .xls) or CSV file');
    }
  };

  const confirmImport = async (mode) => {
    if (!importData?.length) return;
    if (eventRef) {
      try {
        const r = await api.post(`/api/rfx/events/${eventRef}/import-items`, { rows: importData, mode });
        toast.success(r.data.message);
        const ev = await api.get(`/api/rfx/events/${eventRef}`);
        setItems(ev.data.auction_items || ev.data.line_items || []);
      } catch (e) { toast.error(getApiErrorMessage(e, 'Import failed')); }
    } else {
      // Client-side parse
      const newItems = [];
      let lineNum = mode === 'replace' ? 1 : items.length + 1;
      for (const row of importData) {
        const desc = (row.Description || row.description || '').toString().trim();
        if (!desc) continue;
        // Explicit price lookup — handles 0 and all header variants
        const priceKeys = ['Estimated Price', 'Baseline Price', 'Baseline Price (USD)', 'Price', 'Unit Price', 'estimated_price', 'baseline_price'];
        let priceVal = 0;
        for (const pk of priceKeys) {
          if (pk in row && row[pk] !== undefined && row[pk] !== null && row[pk] !== '') {
            priceVal = row[pk]; break;
          }
        }
        newItems.push({
          item_id: `ai_${Date.now()}_${lineNum}`,
          line_number: lineNum,
          description: desc,
          quantity: parseFloat(row.Quantity || row.quantity || 1) || 1,
          uom: (row.UOM || row.uom || 'EA').toString().trim() || 'EA',
          estimated_price: parseFloat(priceVal) || 0,
          lot_id: (row.Lot || row.lot || row.lot_id || '').toString().trim(),
          specifications: (row.Specifications || row.specifications || '').toString().trim(),
        });
        lineNum++;
      }
      const merged = mode === 'replace' ? newItems : [...items, ...newItems];
      setItems(merged);
      if (eventRef) {
        try { await api.post(`/api/rfx/events/${eventRef}/autosave`, { auction_items: merged }); } catch {}
      }
      toast.success(`${newItems.length} item(s) imported`);
    }
    setImportData(null);
    setImportMode(false);
  };

  return (
    <div className="space-y-4 py-2" data-testid="auction-items-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Line Items</h3>
          <p className="text-xs text-slate-500">Define the items or services that suppliers will bid on. Add manually or import from a spreadsheet.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-items-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportMode(!importMode)}
            className={importMode ? 'bg-indigo-50 border-indigo-300' : ''} data-testid="import-items-toggle-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import
          </Button>
          <Button size="sm" onClick={addItem} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-auction-item-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
          </Button>
        </div>
      </div>

      {/* Import Panel — Drag & Drop */}
      {importMode && (
        <div onDrop={handleFileDrop} onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-indigo-300 rounded-lg p-4 bg-indigo-50/50">
          <h4 className="font-medium text-sm mb-2">Import Items from Spreadsheet</h4>
          {!importData && (
            <>
              <div onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.accept = '.xlsx,.xls,.csv'; i.onchange = handleFileUpload; i.click(); }}
                className="border-2 border-dashed border-indigo-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-100/30 transition-colors mb-2">
                <Upload className="w-6 h-6 mx-auto mb-2 text-indigo-400" />
                <p className="text-sm font-medium text-slate-700">Drag & drop Excel or CSV file here</p>
                <p className="text-xs text-slate-400 mt-1">or click to browse — Columns: Description, Quantity, UOM, Estimated Price, Lot, Specifications</p>
              </div>
              {importLoading && <p className="text-sm text-indigo-600">Parsing...</p>}
            </>
          )}
          {importData && (
            <div className="mt-1">
              <p className="text-sm font-medium text-emerald-700 mb-2">{importData.length} item(s) ready to import</p>
              <div className="max-h-48 overflow-y-auto border rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{Object.keys(importData[0] || {}).map(k => <th key={k} className="px-2 py-1 text-left font-medium">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 truncate max-w-[200px]">{String(v)}</td>)}</tr>
                    ))}
                    {importData.length > 10 && <tr><td colSpan={99} className="px-2 py-1 text-slate-400">...and {importData.length - 10} more rows</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => confirmImport('append')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="import-items-append-btn">Append to Existing</Button>
                <Button size="sm" variant="outline" onClick={() => confirmImport('replace')} data-testid="import-items-replace-btn">Replace All</Button>
                <Button size="sm" variant="ghost" onClick={() => { setImportData(null); setImportMode(false); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Items Table */}
      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Archive className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No items yet. Add line items manually or import from a spreadsheet.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-100 border-b">
              <th className="px-3 py-2 text-left w-8">#</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left w-28">Qty</th>
              <th className="px-3 py-2 text-left w-20">UOM</th>
              <th className="px-3 py-2 text-left w-28">Est. Price</th>
              <th className="px-3 py-2 text-left w-32">Lot</th>
              <th className="px-3 py-2 text-left w-40">Specifications</th>
              <th className="px-3 py-2 w-10" />
            </tr></thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.item_id || idx} className="border-b" data-testid={`auction-item-${idx}`}>
                  <td className="px-3 py-2 font-mono text-slate-400">{idx + 1}</td>
                  <td className="px-3 py-1">
                    <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                      placeholder="Item description..." className="h-7 text-xs border-0 bg-transparent focus:bg-white" />
                  </td>
                  <td className="px-3 py-1">
                    <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 1)}
                      className="h-7 text-xs w-28" />
                  </td>
                  <td className="px-3 py-1">
                    <Input value={item.uom} onChange={e => updateItem(idx, 'uom', e.target.value)}
                      className="h-7 text-xs w-16" />
                  </td>
                  <td className="px-3 py-1">
                    <Input type="number" value={item.estimated_price || ''} onChange={e => updateItem(idx, 'estimated_price', parseFloat(e.target.value) || 0)}
                      placeholder="0.00" className="h-7 text-xs w-24" />
                  </td>
                  <td className="px-3 py-1">
                    {lots.length > 0 ? (
                      <Select value={item.lot_id || '__none__'} onValueChange={v => updateItem(idx, 'lot_id', v === '__none__' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No Lot</SelectItem>
                          {lots.map(l => <SelectItem key={l.lot_id} value={l.lot_id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={item.lot_id || ''} onChange={e => updateItem(idx, 'lot_id', e.target.value)}
                        placeholder="Lot..." className="h-7 text-xs w-28" />
                    )}
                  </td>
                  <td className="px-3 py-1">
                    <Input value={item.specifications || ''} onChange={e => updateItem(idx, 'specifications', e.target.value)}
                      placeholder="Specs..." className="h-7 text-xs" />
                  </td>
                  <td className="px-3 py-1 text-center">
                    <button onClick={() => removeItem(idx)} className="text-red-300 hover:text-red-500" aria-label={`Remove item ${idx + 1}`}><X className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 && (
        <div className="text-right text-xs text-slate-500">
          {items.length} item(s) — Estimated total: ${items.reduce((s, i) => s + (i.estimated_price || 0) * (i.quantity || 1), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
      )}
    </div>
  );
}
